import { Link, useLocation } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/auth-provider';
import {
  filterNavForRole,
  isNavGroup,
  navEntries,
  type NavEntry,
  type NavGroup,
  type NavLink,
} from '../nav/nav-items';
import { ChevronDownIcon, HorizontaLDots } from '../../vendor/tailadmin/icons';
import { useSidebar } from '../../vendor/tailadmin/context/SidebarContext';

/**
 * App sidebar — fork of `vendor/tailadmin/layout/AppSidebar.tsx`.
 *
 * MODIFIED FROM UPSTREAM:
 *   - `Link, useLocation` from `react-router` → `@tanstack/react-router`.
 *   - Hardcoded `navItems` / `othersItems` arrays replaced by our role-
 *     filtered `navEntries` from `components/nav/nav-items.ts`
 *     (single source of truth shared with breadcrumbs + tests).
 *   - Two-section Menu/Others header structure collapsed to a single
 *     `<nav aria-label="Hlavní navigace">` block so existing accessibility
 *     contract (used by `sidebar.test.tsx`) holds across the swap.
 *   - Anchor links emit `aria-current="page"` when active (vendor only
 *     toggles CSS classes; we keep semantic active state for screen
 *     readers and tests).
 *   - SidebarWidget (vendor "PRO upsell card") removed — irrelevant for
 *     TENA EDM internal admin UI.
 *   - Demo logo PNG paths (`/images/logo/*.svg`) replaced by inline
 *     text wordmark "TENA EDM" in brand colour, matching our previous
 *     in-house `Sidebar` header.
 *
 * Test contract preserved:
 *   - `<nav aria-label="Hlavní navigace">` wrapper
 *   - Every link uses `<Link aria-current="page" | undefined>`
 *   - Sub-items render in DOM even when the parent group is collapsed
 *     (vendor uses `height: 0px` + `overflow: hidden`, so the nodes are
 *     still queryable via `getByRole('link', { name })`).
 */
export interface AppSidebarProps {
  /**
   * Test/storybook escape hatch — render the sidebar in expanded state
   * regardless of `SidebarContext`. Production always uses the context.
   */
  forceExpanded?: boolean;
}

export function AppSidebar({ forceExpanded = false }: AppSidebarProps) {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const auth = useAuth();

  const showLabels = forceExpanded || isExpanded || isHovered || isMobileOpen;
  const expandedWidth = forceExpanded || isExpanded || isMobileOpen || isHovered;

  const items = useMemo<readonly NavEntry[]>(() => {
    if (!auth.user) return [];
    return filterNavForRole(navEntries, auth.user.role);
  }, [auth.user]);

  // Track which group is currently expanded (only one at a time, matching
  // upstream UX). Auto-open the group containing the active route.
  const [openGroupIndex, setOpenGroupIndex] = useState<number | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<number, number>>({});
  const subMenuRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const isActive = useCallback((path: string) => location.pathname === path, [location.pathname]);

  const isPrefixActive = useCallback(
    (path: string) => location.pathname.startsWith(path),
    [location.pathname],
  );

  // Auto-expand the group whose child matches the current URL.
  useEffect(() => {
    let matched: number | null = null;
    items.forEach((entry, index) => {
      if (isNavGroup(entry)) {
        if (entry.children.some((child) => isActive(child.path))) {
          matched = index;
        }
      }
    });
    if (matched !== null) {
      setOpenGroupIndex(matched);
    }
  }, [items, isActive]);

  // Measure the sub-menu height for the smooth collapse animation
  // (vendor pattern — scrollHeight cached per group on open).
  useEffect(() => {
    if (openGroupIndex !== null) {
      const el = subMenuRefs.current[openGroupIndex];
      if (el) {
        setSubMenuHeight((prev) => ({
          ...prev,
          [openGroupIndex]: el.scrollHeight,
        }));
      }
    }
  }, [openGroupIndex]);

  const handleGroupToggle = (index: number) => {
    setOpenGroupIndex((prev) => (prev === index ? null : index));
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-50 mt-16 flex h-screen flex-col border-r border-gray-200 bg-white px-5 text-gray-900 transition-all duration-300 ease-in-out lg:mt-0 dark:border-gray-800 dark:bg-gray-900
        ${expandedWidth ? 'w-[290px]' : 'w-[90px]'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`flex py-8 ${!showLabels ? 'lg:justify-center' : 'justify-start'}`}>
        <Link to="/" className="text-base font-semibold text-brand-700 dark:text-brand-400">
          {showLabels ? 'TENA EDM' : 'TE'}
        </Link>
      </div>

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        <nav aria-label="Hlavní navigace" className="mb-6">
          <h2
            className={`mb-4 flex text-xs uppercase leading-[20px] text-gray-400 ${
              !showLabels ? 'lg:justify-center' : 'justify-start'
            }`}
          >
            {showLabels ? 'Menu' : <HorizontaLDots className="size-6" />}
          </h2>
          <ul className="flex flex-col gap-4">
            {items.map((entry, index) =>
              isNavGroup(entry) ? (
                <GroupItem
                  key={entry.label}
                  entry={entry}
                  index={index}
                  open={openGroupIndex === index}
                  onToggle={() => handleGroupToggle(index)}
                  showLabels={showLabels}
                  isActive={isActive}
                  isPrefixActive={isPrefixActive}
                  subMenuRefs={subMenuRefs}
                  subMenuHeight={subMenuHeight}
                />
              ) : (
                <LinkItem
                  key={entry.path}
                  entry={entry}
                  active={isActive(entry.path)}
                  showLabels={showLabels}
                />
              ),
            )}
          </ul>
        </nav>
      </div>
    </aside>
  );
}

interface LinkItemProps {
  entry: NavLink;
  active: boolean;
  showLabels: boolean;
}

function LinkItem({ entry, active, showLabels }: LinkItemProps) {
  const Icon = entry.icon;
  return (
    <li>
      <Link
        to={entry.path}
        aria-current={active ? 'page' : undefined}
        className={`menu-item group ${active ? 'menu-item-active' : 'menu-item-inactive'}`}
      >
        {Icon ? (
          <span
            className={`menu-item-icon-size ${
              active ? 'menu-item-icon-active' : 'menu-item-icon-inactive'
            }`}
          >
            <Icon />
          </span>
        ) : null}
        {showLabels ? <span className="menu-item-text">{entry.label}</span> : null}
      </Link>
    </li>
  );
}

interface GroupItemProps {
  entry: NavGroup;
  index: number;
  open: boolean;
  onToggle: () => void;
  showLabels: boolean;
  isActive: (path: string) => boolean;
  isPrefixActive: (path: string) => boolean;
  subMenuRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  subMenuHeight: Record<number, number>;
}

function GroupItem({
  entry,
  index,
  open,
  onToggle,
  showLabels,
  isActive,
  isPrefixActive,
  subMenuRefs,
  subMenuHeight,
}: GroupItemProps) {
  const Icon = entry.icon;
  const groupActive = entry.children.some((c) => isPrefixActive(c.path));

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`menu-item group ${
          groupActive ? 'menu-item-active' : 'menu-item-inactive'
        } cursor-pointer ${showLabels ? 'lg:justify-start' : 'lg:justify-center'}`}
      >
        {Icon ? (
          <span
            className={`menu-item-icon-size ${
              groupActive ? 'menu-item-icon-active' : 'menu-item-icon-inactive'
            }`}
          >
            <Icon />
          </span>
        ) : null}
        {showLabels ? <span className="menu-item-text">{entry.label}</span> : null}
        {showLabels ? (
          <ChevronDownIcon
            className={`ml-auto h-5 w-5 transition-transform duration-200 ${
              open ? 'rotate-180 text-brand-600 dark:text-brand-400' : ''
            }`}
          />
        ) : null}
      </button>

      <div
        ref={(el) => {
          subMenuRefs.current[index] = el;
        }}
        className="overflow-hidden transition-all duration-300"
        style={{
          height: open && showLabels ? `${subMenuHeight[index] ?? 0}px` : '0px',
        }}
      >
        <ul className="ml-9 mt-2 space-y-1">
          {entry.children.map((child) => {
            const active = isActive(child.path);
            return (
              <li key={child.path}>
                <Link
                  to={child.path}
                  aria-current={active ? 'page' : undefined}
                  className={`menu-dropdown-item ${
                    active ? 'menu-dropdown-item-active' : 'menu-dropdown-item-inactive'
                  }`}
                >
                  {child.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
}
